import { Appointments } from '@devexpress/dx-react-scheduler-material-ui';
import { IBookingType } from './AppointmentComponent';
import { useContext } from 'react';
import { findFormatter } from '../../../../../../../../Utils/Formatters/FindFormatter';
import EDTooltip from '../../../../../../../../BaseComponents/Tooltip/EDTooltip';
import EDIcon from '../../../../../../../../BaseComponents/Icon/EDIcon';
import { SchedulerContext } from '../../SchedulerContext';
import { makeStyles } from 'tss-react/mui';
import { Theme } from '@mui/material/styles';
import {
  useIsExtraSmallScreen,
  useIsSmallScreen,
} from '../../../../../../../../Hooks/useFullscreenView';
import { useTheme } from '@mui/material';
import {
  ActivityIcons,
  hasActivityIcons,
} from '../Utils/ActivityIconsSelector';

const getBackgroundColor = (
  theme: Theme,
  type: string | undefined,
  prelBokad: boolean | null | undefined,
  bokningsbar: boolean | null
): string | undefined => {
  const colors = theme.customProperties.AktivitetColors as unknown as Record<
    string,
    string | Record<string, string>
  >;
  if (type && !prelBokad) {
    if (bokningsbar == true) {
      return (colors.bokningsbar as Record<string, string>)[type];
    } else return colors[type] as string;
  } else if (type && prelBokad) {
    if (bokningsbar == true) {
      return (colors.bokningsbar as Record<string, string>)[type];
    } else return (colors.preliminar as Record<string, string>)[type];
  } else {
    return undefined;
  }
};

const useStyles = makeStyles()(theme => ({
  appointmentResourceContainer: {
    fontWeight: theme.typography.fontWeightLight,
  },
  dayName: {
    background: theme.customProperties.AktivitetColors.dayName,
  },
  redDay: {
    color: 'red',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  dayNameOverflow: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

export const CustomAppointments = (
  props: Appointments.AppointmentContentProps
) => {
  const isExtraSmall = useIsExtraSmallScreen();
  const data = props.data as IBookingType;
  const color = data?.aktivityType?.toLowerCase();
  const prelBokad = data.prelBokad;
  const bokningsbar = data.bokningsbar;
  const theme = useTheme();
  const { classes, cx } = useStyles();
  const schedulerContext = useContext(SchedulerContext);
  const isSmallScreen = useIsSmallScreen();

  let isStallTid = false;
  let calculatedTop: number = 0;
  let calculatedHeight: number = 0;

  if (
    schedulerContext.currentViewName !== 'Month' &&
    data.aktStartDate &&
    data.aktEndDate &&
    (data.aktStartDate !== data.startDate || data.aktEndDate !== data.endDate)
  ) {
    isStallTid = true;
    const parseAktStartDate = new Date(data.aktStartDate).valueOf();
    const parseStartDate = new Date(data.startDate).valueOf();
    const parseAktEndDate = new Date(data.aktEndDate).valueOf();
    const parseEndDate = data.endDate && new Date(data.endDate).valueOf();
    const appointmentContentStart = Math.max(parseAktStartDate, parseStartDate);
    const appointmentContentEnd = parseEndDate
      ? Math.min(parseAktEndDate, parseEndDate)
      : parseAktEndDate;
    calculatedTop = Math.floor(
      (47 * (appointmentContentStart - parseStartDate)) / 1000 / 60 / 60
    );
    calculatedHeight = Math.floor(
      (47 * (appointmentContentEnd - appointmentContentStart)) / 1000 / 60 / 60
    );
  }

  const parseDates = (data: IBookingType) => {
    const startDate = findFormatter(
      'TEDFormatDateTimeGeneral',
      'Date'
    ).displayer.display(
      data.aktStartDate?.toString(),
      undefined,
      'Format="dd-mm";DateSep="/"'
    );
    const endDate = findFormatter(
      'TEDFormatDateTimeGeneral',
      'Date'
    ).displayer.display(
      data.aktEndDate?.toString(),
      undefined,
      'Format="dd-mm";DateSep="/"'
    );

    return (
      <div>
        {startDate}-{endDate}
      </div>
    );
  };

  const parseTimes = (data: IBookingType) => {
    const startDate = findFormatter(
      'TEDFormatDateTimeGeneral',
      'Date'
    ).displayer.display(
      data.aktStartDate?.toString(),
      undefined,
      'Format="hh:nn"'
    );
    const endDate = findFormatter(
      'TEDFormatDateTimeGeneral',
      'Date'
    ).displayer.display(
      data.aktEndDate?.toString(),
      undefined,
      'Format="hh:nn"'
    );

    return (
      <div>
        {startDate}-{endDate}
      </div>
    );
  };

  const bookingTime = parseTimes(data);
  const showIcons = hasActivityIcons(props.data?.activity) && !isSmallScreen;

  return (
    <Appointments.AppointmentContent
      key={data.bookingId}
      className={cx({
        [classes.dayName]: data.dayName,
      })}
      testId={'Appointment'}
      style={{
        whiteSpace: 'nowrap',
        flexDirection: 'column',
        top: isStallTid && !data.allDay ? calculatedTop : 'inherit',
        height: isStallTid && !data.allDay ? calculatedHeight : 'inherit',
        position: isStallTid && !data.allDay ? 'absolute' : 'initial',
        boxSizing: 'border-box',
        width: isStallTid && !data.allDay ? 'inherit' : 'inherit',
        color: 'black',
        background:
          !data.dayName && !props.data.mooreBookings
            ? getBackgroundColor(theme, color, prelBokad, bokningsbar)
            : undefined,
      }}
      {...props}
    >
      {props.data.mooreBookings ? (
        <EDTooltip placement="bottom" title="Visa alla aktiviteter">
          <div>
            <EDIcon color="primary" iconType="more_horiz" />
          </div>
        </EDTooltip>
      ) : (
        <>
          <div
            style={{ position: 'relative', width: '100% !important' }}
            className={cx({
              [classes.redDay]: data.redDay,
              [classes.dayNameOverflow]: data.dayName && isExtraSmall,
            })}
          >
            <div
              style={{
                paddingRight: '0.25rem',
                display: 'flex',
                alignItems: 'start',
              }}
            >
              <div
                style={{
                  maxWidth: showIcons
                    ? isSmallScreen
                      ? '50%'
                      : '80%'
                    : '100%',
                  inlineSize: showIcons
                    ? isSmallScreen
                      ? '50%'
                      : '80%'
                    : '100%',
                  whiteSpace: data.dayName ? 'nowrap' : 'pre-wrap',
                  overflowWrap: 'break-word',
                  textOverflow: data.dayName ? 'ellipsis' : undefined,
                  overflow: data.dayName ? 'hidden' : undefined,
                }}
              >
                {props.data.title}
              </div>
              <div style={{ marginLeft: 'auto' }}>
                {showIcons && (
                  <ActivityIcons
                    activity={props.data.activity}
                    size="small"
                    showTooltips={false}
                  />
                )}
              </div>
            </div>
          </div>
          {data.allDay && !data.dayName ? parseDates(data) : undefined}
          {!data.dayName ? bookingTime : undefined}
          {!props.data.mooreBookings && data.location?.resName ? (
            <div className={classes.appointmentResourceContainer}>
              {data.location?.resName}
            </div>
          ) : undefined}
        </>
      )}
    </Appointments.AppointmentContent>
  );
};
